import { useState } from 'react';
import { Award, BarChart3, ChevronRight, Trophy, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { BadgeTone } from '@/components/ui/Badge';
import { gradeFor, gradeToneClasses } from '@/constants/grading';
import type { OverviewPerformance, OverviewPerformer } from '@/types/teacherDashboard';
import { getNameInitials } from '@/lib/utils';

interface BestPerformersCardProps {
  /**
   * One entry per section-and-subject the teacher holds that has marks against
   * it. An allotment nobody has been marked in yet is not in this list — it
   * would otherwise be drawn as a class averaging zero.
   */
  items: OverviewPerformance[];
  isLoading: boolean;
}

/** The bar fill for each grade band's tone. */
const BAR_TONE: Record<BadgeTone, string> = {
  success: 'bg-emerald-500',
  info: 'bg-blue-600',
  warning: 'bg-amber-500',
  critical: 'bg-rose-500',
  neutral: 'bg-ink-3',
  accent: 'bg-accent',
};

/**
 * The colour a class average is drawn in.
 *
 * Banded rather than picked per class, so two sections scoring the same are
 * always the same colour and a teacher can read the card without the legend.
 * The bands are the institute's grade scale (`gradeFor`), so a class average
 * drawn amber here is the same "C" the marks screens call it — the card's own
 * 85/70/50 cut-offs disagreed with every grade badge in the console.
 */
function barColorClass(percentage: number): string {
  return BAR_TONE[gradeFor(percentage, 100)?.tone ?? 'neutral'];
}

/**
 * A child's place among the class's top performers, ties sharing it.
 *
 * Competition ranking ("1, 1, 3"): the server breaks a tie alphabetically, so
 * reading the place off the list position handed two children on the same
 * score different medals.
 */
function rankOf(percentage: number, ranked: OverviewPerformer[]): number {
  return 1 + ranked.filter((other) => other.percentage > percentage).length;
}

function rankMedal(rank: number): string {
  switch (rank) {
    case 1:
      return '🥇';
    case 2:
      return '🥈';
    case 3:
      return '🥉';
    default:
      return '⭐';
  }
}

export function BestPerformersCard({ items, isLoading }: BestPerformersCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClassFilter, setSelectedClassFilter] = useState('ALL');

  const filteredModalClasses = items.filter((item) => {
    if (selectedClassFilter !== 'ALL' && item.displayName !== selectedClassFilter) {
      return false;
    }
    return true;
  });

  return (
    <>
      <div className="card bg-surface border border-line rounded-[20px] p-5 shadow-2xs flex flex-col gap-4">
        {/* Header with Title & Subtitle */}
        <div className="flex items-center justify-between border-b border-line pb-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-4.5 w-4.5 text-amber-500" />
            <div>
              <h3 className="text-base font-bold text-ink tracking-tight">Class Performance</h3>
            </div>
          </div>
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="text-xs font-bold text-accent hover:underline flex items-center gap-0.5 cursor-pointer"
            >
              View All <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Performers Rows: Showing Subject Class Average Progress Bar + Top 3 Student Faces */}
        {isLoading ? (
          <SkeletonLines count={3} />
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3 font-medium">
            No marks entered yet. Class averages appear once an exam has been marked.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {items.slice(0, 3).map((item) => (
              <div key={item.allocationUuid} className="flex items-center justify-between gap-3">
                {/* Class Name */}
                <span className="text-xs font-extrabold text-ink min-w-[70px]">
                  {item.displayName}
                </span>

                {/* Progress Bar Track representing Class Subject Average Score */}
                <div className="flex-1 relative flex items-center bg-surface-2 rounded-full h-6 border border-line p-0.5">
                  {/* Colored Fill Bar representing Subject Class Average Score % */}
                  <div className="w-full h-full rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${barColorClass(item.averagePercentage)}`}
                      style={{ width: `${item.averagePercentage}%` }}
                    />
                  </div>

                  {/* Up to 3 Small Overlapping Initials at Start of Track with Hover Popout & Tooltip */}
                  <div className="absolute left-1.5 flex items-center -space-x-1.5 z-10">
                    {item.topStudents.slice(0, 3).map((student) => (
                      <div key={student.uuid} className="relative group hover:z-30">
                        <span
                          aria-hidden
                          className="grid h-5 w-5 place-items-center rounded-full bg-surface text-accent text-3xs font-black ring-2 ring-surface shadow-2xs cursor-pointer group-hover:scale-125 group-hover:bg-accent group-hover:text-white group-hover:ring-surface group-hover:shadow-md transition-all duration-200"
                        >
                          {getNameInitials(student.fullName)}
                        </span>

                        {/* Tooltip Popout displaying Student Name & Individual Score on Hover */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center pointer-events-none z-50 animate-fade-in">
                          <div className="bg-ink text-surface text-3xs font-extrabold px-2.5 py-1 rounded-pill shadow-lg whitespace-nowrap flex items-center gap-1.5 border border-surface/20">
                            <span>{student.fullName}</span>
                            <span
                              className={`font-mono ${gradeToneClasses(gradeFor(student.percentage, 100)).text}`}
                            >
                              ({student.percentage}%)
                            </span>
                          </div>
                          <span className="w-1.5 h-1.5 bg-ink rotate-45 -mt-1 shadow-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Class Average Score Badge (Explicitly labeled as 'Avg') */}
                <span className="text-xs font-black text-ink-2 min-w-[54px] text-right font-mono">
                  {item.averagePercentage}% Avg
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VIEW ALL ALLOCATED CLASSES PERFORMANCE MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-up">
          <div className="w-full max-w-2xl overflow-hidden rounded-[20px] bg-surface border border-line shadow-2xl flex flex-col max-h-[85vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-line bg-surface-2 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-amber-500/15 text-amber-600 border border-amber-500/30">
                  <Trophy className="h-4.5 w-4.5" />
                </span>
                <div>
                  <h3 className="text-base font-bold text-ink">Allocated Classes Performance</h3>
                  <p className="text-xs text-ink-3 font-medium">Class Academic Average & Top 3 Student Performers</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="rounded-field p-1.5 text-ink-3 hover:bg-surface-3 hover:text-ink transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Class Filter Bar */}
            <div className="px-6 py-3 border-b border-line bg-surface flex items-center justify-between">
              <div className="flex items-center gap-1.5 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setSelectedClassFilter('ALL')}
                  className={`px-3 py-1 rounded-pill text-xs font-bold transition-all ${
                    selectedClassFilter === 'ALL'
                      ? 'bg-accent text-white shadow-2xs'
                      : 'bg-surface-2 text-ink-3 hover:text-ink border border-line'
                  }`}
                >
                  All Classes
                </button>
                {/* One button per section, not per allotment: a teacher who takes
                    two subjects in 8-A would otherwise see it listed twice. */}
                {[...new Set(items.map((item) => item.displayName))].map((displayName) => (
                  <button
                    key={displayName}
                    type="button"
                    onClick={() => setSelectedClassFilter(displayName)}
                    className={`px-3 py-1 rounded-pill text-xs font-bold transition-all ${
                      selectedClassFilter === displayName
                        ? 'bg-accent text-white shadow-2xs'
                        : 'bg-surface-2 text-ink-3 hover:text-ink border border-line'
                    }`}
                  >
                    {displayName}
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Body: Clean Production Institute Software Layout */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col divide-y divide-line">
              {filteredModalClasses.length > 0 ? (
                filteredModalClasses.map((item) => (
                  <div
                    key={item.allocationUuid}
                    className="py-5 first:pt-0 last:pb-0 flex flex-col gap-3.5"
                  >
                    {/* Class Header with Explicit Metrics */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <h4 className="text-sm font-bold text-ink">{item.displayName}</h4>
                        <span className="text-xs font-semibold text-accent bg-accent/10 px-2.5 py-0.5 rounded-pill border border-accent/20">
                          {item.subjectName}
                        </span>
                        <span className="text-xs font-semibold text-emerald-600 bg-emerald-500/15 px-2.5 py-0.5 rounded-pill border border-emerald-500/30">
                          {item.passRate}% of papers passed
                        </span>
                      </div>
                    </div>

                    {/* Class Average Progress Bar with Explicit Label */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-xs font-semibold text-ink-3">
                        <span className="text-3xs font-extrabold uppercase tracking-wider text-ink-3">
                          Class Average Score
                        </span>
                        <span className="text-xs font-bold font-mono text-ink">
                          {item.averagePercentage}%
                        </span>
                      </div>

                      <div className="w-full bg-surface-2 rounded-full h-2.5 overflow-hidden border border-line p-0.5">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${barColorClass(item.averagePercentage)}`}
                          style={{ width: `${item.averagePercentage}%` }}
                        />
                      </div>
                    </div>

                    {/* Top 3 Performers (Cleaned up subheading, removed repeating subject name) */}
                    <div className="mt-1">
                      <h5 className="text-xs font-semibold text-ink-3 mb-2.5 flex items-center gap-1.5">
                        <Award className="h-3.5 w-3.5 text-amber-500" /> Top Performers
                      </h5>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {item.topStudents.map((student) => (
                          <div
                            key={student.uuid}
                            className="p-3 rounded-[12px] bg-surface-2/40 border border-line flex items-center justify-between gap-2 shadow-2xs hover:border-accent/30 transition-all"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              {/* Initials with the Medal Hanging on Bottom-Right Corner */}
                              <div className="relative shrink-0">
                                <span
                                  aria-hidden
                                  className="grid h-9 w-9 place-items-center rounded-full bg-accent/15 text-accent text-2xs font-black border border-line shadow-2xs"
                                >
                                  {getNameInitials(student.fullName)}
                                </span>
                                <span className="absolute -bottom-1 -right-1 text-xs leading-none">
                                  {rankMedal(rankOf(student.percentage, item.topStudents))}
                                </span>
                              </div>

                              {/* Name (Single Line) + Small Roll No */}
                              <div className="min-w-0 flex flex-col justify-center">
                                <h6 className="text-xs font-bold text-ink whitespace-nowrap truncate leading-tight">
                                  {student.fullName}
                                </h6>
                                <span className="text-[10px] font-medium font-mono text-ink-3 mt-0.5">
                                  {student.rollNumber ? `Roll #${student.rollNumber}` : 'No roll no.'}
                                </span>
                              </div>
                            </div>

                            {/* Individual Student Score Badge */}
                            <span
                              className={`text-xs font-black font-mono px-2 py-0.5 rounded-field border shrink-0 ${gradeToneClasses(gradeFor(student.percentage, 100)).badge}`}
                            >
                              {student.percentage}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-xs text-ink-3 italic bg-surface-2/40 rounded-field">
                  No matching allocated classes found.
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-line bg-surface-2 px-6 py-3.5 flex items-center justify-between">
              <span className="text-xs text-ink-3 font-medium flex items-center gap-1.5">
                <BarChart3 className="h-4 w-4 text-accent" /> Academic performance metrics for allocated classes.
              </span>
              <Button variant="secondary" size="sm" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
